import { FC, useEffect, useRef } from 'react';
import { NativeScrollProps } from './NativeScroll.props';
import { StyledContent, StyledInner, StyledScrollbar, StyledWrapper } from './NativeScroll.styles';

const NativeScroll: FC<NativeScrollProps> = props => {
  const { children, id, className, onScrollX, containerRef, options = {} } = props;
  const { suppressScrollY = true, suppressScrollX = false } = options;

  const contentRef = useRef<HTMLDivElement | null>(null);
  const fakeRef = useRef<HTMLDivElement | null>(null);

  // Flags to prevent circular scroll syncing (fake -> content -> fake)
  const isSyncingFromContent = useRef(false);
  const isSyncingFromFake = useRef(false);

  // Stores the requestAnimationFrame ID for throttled syncing
  const requestAnimationFrameId = useRef<number | null>(null);

  // Syncs scrollLeft using RAF to avoid layout thrash
  const syncScrollLeft = (target: HTMLElement, left: number): void => {
    const el = target; // avoid param mutation

    if (requestAnimationFrameId.current) {
      cancelAnimationFrame(requestAnimationFrameId.current);
    }

    requestAnimationFrameId.current = requestAnimationFrame(() => {
      el.scrollLeft = left;
    });
  };

  // When the fake scrollbar moves, sync the real content
  const handleFakeScroll = (): void => {
    if (!fakeRef.current || !contentRef.current || suppressScrollX || isSyncingFromContent.current) {
      return;
    }

    isSyncingFromFake.current = true;

    const left = fakeRef.current.scrollLeft;
    syncScrollLeft(contentRef.current, left);

    if (onScrollX) {
      onScrollX(left);
    }

    isSyncingFromFake.current = false;
  };

  // When the real content scrolls, sync the fake scrollbar
  const handleContentScroll = (): void => {
    if (!contentRef.current || !fakeRef.current || suppressScrollX || isSyncingFromFake.current) {
      return;
    }

    isSyncingFromContent.current = true;

    const left = contentRef.current.scrollLeft;
    syncScrollLeft(fakeRef.current, left);

    if (onScrollX) {
      onScrollX(left);
    }

    isSyncingFromContent.current = false;
  };

  // Expose the fake scrollbar element to parent components
  useEffect(() => {
    if (containerRef) {
      containerRef(fakeRef.current);
    }
  }, [containerRef]);

  // Syncs widths between content and fake scrollbar using ResizeObserver + RAF
  useEffect(() => {
    let cleanup = () => {}; // fallback cleanup

    if (contentRef.current && fakeRef.current) {
      let resizeObserver: ResizeObserver | null = null;
      let ObserverRequestAnimationFrame: number | null = null;

      const update = (): void => {
        if (!contentRef.current || !fakeRef.current) {
          return;
        }

        const clientW = contentRef.current.clientWidth;
        const scrollW = contentRef.current.scrollWidth;

        fakeRef.current.style.width = `${clientW}px`;

        const inner = fakeRef.current.firstElementChild as HTMLElement | null;

        if (inner) {
          const next = `${scrollW}px`;

          if (inner.style.width !== next) {
            inner.style.width = next;
          }
        }
      };

      update();

      resizeObserver = new ResizeObserver(() => {
        if (ObserverRequestAnimationFrame) {
          cancelAnimationFrame(ObserverRequestAnimationFrame);
        }
        ObserverRequestAnimationFrame = requestAnimationFrame(update);
      });

      resizeObserver.observe(contentRef.current);

      cleanup = () => {
        if (ObserverRequestAnimationFrame) {
          cancelAnimationFrame(ObserverRequestAnimationFrame);
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }

    return cleanup;
  }, []);

  // Controls vertical scroll based on options
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.overflowY = suppressScrollY ? 'hidden' : 'auto';
    }
  }, [suppressScrollY]);

  // Controls horizontal scroll based on options
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.overflowX = suppressScrollX ? 'hidden' : 'auto';
    }
    if (fakeRef.current) {
      fakeRef.current.style.display = suppressScrollX ? 'none' : 'block';
    }
  }, [suppressScrollX]);

  const preventFocus: React.FocusEventHandler = e => {
    // If something tries to focus the scroll containers, immediately blur it.
    (e.currentTarget as HTMLElement).blur();
  };

  return (
    <StyledWrapper
      id={id}
      className={className}
      data-testid="native-scroll-wrapper"
      data-suppress-scroll-x={suppressScrollX}>
      {/* Real content container */}
      <StyledContent data-testid="native-scroll-content" onScroll={handleContentScroll} ref={contentRef} tabIndex={-1}>
        {children}
      </StyledContent>

      {/* Fake stylable horizontal scrollbar */}
      <StyledScrollbar
        aria-hidden="true"
        data-testid="native-scroll-scrollbar"
        onFocus={preventFocus}
        onScroll={handleFakeScroll}
        ref={fakeRef}
        tabIndex={-1}>
        <StyledInner data-testid="native-scroll-inner" />
      </StyledScrollbar>
    </StyledWrapper>
  );
};

export default NativeScroll;
